/**
 * ArtifactUploader – Reusable file upload component for the global artifact system.
 *
 * Renders an "Attach" button that opens a file picker (with camera on mobile).
 * Handles upload via the global artifact pipeline with loading state + toast feedback.
 */

import { useRef, useState } from 'react';
import { Paperclip, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUploadArtifact } from '@/hooks/useArtifacts';
import { ARTIFACT_SOURCES } from '@/lib/artifacts/constants';
import type { LinkEntityType, LinkRole, ArtifactSource } from '@/lib/artifacts/constants';
import { toast } from 'sonner';

interface ArtifactUploaderProps {
  shopId: string;
  entityType: LinkEntityType;
  entityId: string;
  role: LinkRole;
  source?: ArtifactSource;
  accept?: string;
  className?: string;
  label?: string;
}

export function ArtifactUploader({
  shopId,
  entityType,
  entityId,
  role,
  source = ARTIFACT_SOURCES.PWA_UPLOAD,
  accept = 'image/*,video/*,application/pdf,.pdf',
  className,
  label = 'Attach',
}: ArtifactUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadArtifact();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadMutation.mutateAsync({
        shopId,
        file,
        source,
        link: { entityType, entityId, role },
      });
      toast.success('Evidence attached');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset input so user can re-upload same file
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className={className}
      >
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
        ) : (
          <Paperclip className="h-3.5 w-3.5 mr-1" />
        )}
        {label}
      </Button>
    </>
  );
}
